import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserStore } from '../stores/useUserStore';
import { useRBAC } from '../hooks/useRBAC';
import authProvider from '../authProvider';

import { ModernHeader } from '../components/ModernHeader';
import {
  ModernSidebar,
  ModernMobileNavigation,
} from '../components/ModernNavigation';
import PasskeyRegistrationDialog from './PasskeyRegistrationDialog';
import { PreviewModeIndicator } from '../components/PreviewModeIndicator';
import { useSensitiveDataBlur } from '../hooks/useSensitiveDataBlur';
import { AssistantChat } from '../components/AssistantChat';
import { DevConsole } from '../components/DevConsole';
import { GlobalSettingsLoader } from '../components/GlobalSettingsLoader';

interface ModernLayoutProps {
  children: React.ReactNode;
}

const ModernLayout: React.FC<ModernLayoutProps> = ({ children }) => {
  const [showPasskeyDialog, setShowPasskeyDialog] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const { user, fetchUser } = useUserStore();
  const { highestRole } = useRBAC();
  const location = useLocation();

  // Initialize sensitive data blurring hook at layout level
  // This ensures blurring is active globally, including preview mode
  useSensitiveDataBlur();

  // Check for preview mode to adjust layout
  // Preview mode is tab-specific (using sessionStorage and URL params)
  useEffect(() => {
    const checkPreviewMode = () => {
      // Check URL parameter first (for initial load)
      const urlParams = new URLSearchParams(window.location.search);
      const hasPreviewParam = urlParams.get('preview') === 'true';

      // Check sessionStorage (tab-specific storage)
      const previewMode = sessionStorage.getItem('sensitive-data-preview-mode');

      setIsPreviewMode(previewMode === 'true' || hasPreviewParam);
    };

    checkPreviewMode();

    // Check periodically for same-tab navigation (URL might change)
    const interval = setInterval(checkPreviewMode, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // Ensure user data is loaded
    if (!user) {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // fetchUser is stable from Zustand store, no need to include in deps

  useEffect(() => {
    // Check if we should show the dialog when user is loaded
    const checkPasskeyStatus = async () => {
      if (user) {
        const hasShownPasskeyPrompt = localStorage.getItem(
          'hasShownPasskeyPrompt'
        );
        if (!hasShownPasskeyPrompt) {
          try {
            const hasPasskey = await authProvider.auth.hasPasskey();
            if (!hasPasskey) {
              setShowPasskeyDialog(true);
            } else {
              localStorage.setItem('hasShownPasskeyPrompt', 'true');
            }
          } catch (error) {
            console.error('Error checking passkey status:', error);
            // For errors, don't show the dialog to avoid confusion
            localStorage.setItem('hasShownPasskeyPrompt', 'true');
          }
        }
      }
    };

    checkPasskeyStatus();
  }, [user]);

  const handleRegisterPasskey = async () => {
    try {
      await authProvider.auth.registerPasskey();
      console.log('Passkey registered successfully');
      setShowPasskeyDialog(false);
      localStorage.setItem('hasShownPasskeyPrompt', 'true');
    } catch (error) {
      console.error('Passkey registration error:', error);
    }
  };

  const handleClosePasskeyDialog = () => {
    setShowPasskeyDialog(false);
    localStorage.setItem('hasShownPasskeyPrompt', 'true');
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <>
      <GlobalSettingsLoader />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <ModernHeader onSidebarToggle={handleSidebarToggle} />

        {/* Preview Mode Indicator */}
        <PreviewModeIndicator />

        <div
          className={`flex ${
            isPreviewMode
              ? 'h-[calc(100vh-3.5rem-3.5rem)]'
              : 'h-[calc(100vh-3.5rem)]'
          } ${isPreviewMode ? 'mt-14' : ''}`}
        >
          {/* Sidebar - Desktop */}
          <div className="hidden lg:block">
            <ModernSidebar
              isCollapsed={isSidebarCollapsed}
              onToggle={handleSidebarToggle}
            />
          </div>

          {/* Main Content */}
          <main className="flex-1 overflow-auto">
            <div className="container mx-auto p-4">{children}</div>
          </main>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <ModernMobileNavigation />
          </div>
        </div>
      </div>

      {/* Passkey Registration Dialog */}
      <PasskeyRegistrationDialog
        open={showPasskeyDialog}
        onClose={handleClosePasskeyDialog}
        onRegister={handleRegisterPasskey}
      />

      {/* Virtual Assistant Chat - Hidden for staff */}
      {user && highestRole !== 'staff' && <AssistantChat />}

      {/* Dev Console (Development Mode Only) */}
      <DevConsole />
    </>
  );
};

export default ModernLayout;
